/**
 * First-run page tours ("like Windows after a fresh install").
 *
 * A tour is a list of steps; each step spotlights one element on the page
 * (found by its data-tour="key" attribute), explains it in a card, and either
 * waits for Next or asks the user to click the highlighted element itself
 * (action: 'click'). A click step can hand over to another page's tour
 * (`then: 'owner-console'`), so a tour can say "open the menu, now click
 * Settings" and carry on there after the navigation.
 *
 * Progress is stored on the user (PUT /api/users/profile/tours) as
 * { seen: { pageKey: date }, skippedAll, remindAfter }; a page's tour starts by
 * itself the first time that page is opened (see usePageTour), and can be
 * replayed from the Help (?) menu at any time.
 *
 * Step shape (see tours/pages/*.js):
 *   { target: 'data-tour key' | null, title, body, placement?, action?: 'next'|'click',
 *     then?: nextTourKey, when?: (ctx) => bool, timeout?: ms }
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, Paper, Popper, Typography, Link, Fade } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useOrg } from '../context/OrgContext';
import { settingsAccess } from '../utils/settingsAccess';
import { getTour } from './registry';
import { saveTours, normalizeTours } from './toursApi';

const TourContext = createContext(null);

// A tour that must start on the next page (set by a click step with `then`, by the
// Help page's "Run the tour" button, and by the setup wizard's Finish)
const CHAIN_KEY = 'wk.tour.next';
export const queueTour = (pageKey) => { try { sessionStorage.setItem(CHAIN_KEY, pageKey); } catch (e) { /* ignore */ } };
const peekQueued = () => { try { return sessionStorage.getItem(CHAIN_KEY); } catch (e) { return null; } };
const clearQueued = () => { try { sessionStorage.removeItem(CHAIN_KEY); } catch (e) { /* ignore */ } };

const REMIND_DAYS = 3;
// Above the side drawer (1200) so menu items can be spotlighted; below dialogs (1300),
// so a dialog a step opens (new job, unlock) is usable and the tour waits behind it
const Z = 1250;
const PAD = 6;            // spotlight padding around the target
const POLL_MS = 120;
const sel = (key) => `[data-tour="${key}"]`;

const sameRect = (a, b) => a && b && ['top', 'left', 'width', 'height'].every((k) => Math.round(a[k]) === Math.round(b[k]));

export function TourProvider({ children }) {
  const { currentUser } = useUser();
  const org = useOrg();
  const location = useLocation();

  // Local copy of the user's tour state; the API is updated in the background
  const [tours, setTours] = useState(null);
  useEffect(() => {
    if (!currentUser) { setTours(null); return; }
    setTours((prev) => prev || normalizeTours(currentUser.tours));
  }, [currentUser]);
  const toursRef = useRef(null);
  toursRef.current = tours;

  const persist = useCallback((patch, next) => {
    setTours(next);
    toursRef.current = next;
    saveTours(patch).catch(() => { /* progress is a convenience; the next visit retries */ });
  }, []);

  const markSeen = useCallback((pageKey) => {
    const cur = toursRef.current || normalizeTours(null);
    if (cur.seen[pageKey]) return;
    persist({ seen: { [pageKey]: true } }, { ...cur, seen: { ...cur.seen, [pageKey]: new Date().toISOString() } });
  }, [persist]);

  const skipAll = useCallback(() => {
    const cur = toursRef.current || normalizeTours(null);
    persist({ skippedAll: true }, { ...cur, skippedAll: true });
  }, [persist]);

  const remindLater = useCallback(() => {
    const cur = toursRef.current || normalizeTours(null);
    const when = new Date(Date.now() + REMIND_DAYS * 86400000).toISOString();
    persist({ remindAfter: when }, { ...cur, remindAfter: when });
  }, [persist]);

  const resetAll = useCallback(() => {
    persist({ reset: true }, normalizeTours(null));
  }, [persist]);

  // ---- the running tour --------------------------------------------------------------
  const [run, setRun] = useState(null); // { pageKey, steps, index, variant }
  const [target, setTarget] = useState(null); // DOM element of the current step
  const [rect, setRect] = useState(null);
  const [missing, setMissing] = useState(false); // target never appeared → step is skipped
  const [lookNonce, setLookNonce] = useState(0); // bumped when a found target disappears, to look again
  const currentPage = useRef(null); // which page's tour would "replay" from the Help menu

  const ctx = useMemo(() => ({
    user: currentUser,
    access: settingsAccess(currentUser),
    isPlatform: (currentUser?.orgKind || 'platform') === 'platform',
    companyName: org?.name || currentUser?.organization?.name || 'White Knight',
    demoMode: !!org?.demoMode
  }), [currentUser, org]);

  const buildSteps = useCallback((pageKey, variant) => {
    const tour = getTour(pageKey);
    if (!tour || !currentUser) return null;
    const steps = tour.steps({ ...ctx, variant }).filter((s) => !s.when || s.when({ ...ctx, variant }));
    return steps.length ? { title: tour.title, steps } : null;
  }, [ctx, currentUser]);

  const startTour = useCallback((pageKey, { variant } = {}) => {
    const built = buildSteps(pageKey, variant);
    if (!built) return false;
    setRun({ pageKey, title: built.title, steps: built.steps, index: 0, variant });
    return true;
  }, [buildSteps]);

  const endTour = useCallback((reason = 'done') => {
    setRun((r) => {
      if (r && reason !== 'later') markSeen(r.pageKey);
      return null;
    });
    setTarget(null); setRect(null); setMissing(false);
  }, [markSeen]);

  const goTo = useCallback((index) => {
    setRun((r) => {
      if (!r) return r;
      if (index >= r.steps.length) { markSeen(r.pageKey); return null; }
      return { ...r, index: Math.max(0, index) };
    });
    setTarget(null); setRect(null); setMissing(false);
  }, [markSeen]);

  const next = useCallback(() => setRun((r) => {
    if (!r) return r;
    if (r.index + 1 >= r.steps.length) { markSeen(r.pageKey); return null; }
    return { ...r, index: r.index + 1 };
  }), [markSeen]);

  const step = run ? run.steps[run.index] : null;

  // Hand over to another page's tour when a click step says so
  const chainTo = useCallback((tourKey) => {
    queueTour(tourKey);
    setRun((r) => { if (r) markSeen(r.pageKey); return null; });
    setTarget(null); setRect(null); setMissing(false);
  }, [markSeen]);

  // Find the step's target, waiting for it to appear (drawers and tabs render late)
  useEffect(() => {
    setTarget(null); setRect(null); setMissing(false);
    if (!step) return undefined;
    if (!step.target) return undefined; // a centred card, no spotlight
    let cancelled = false;
    const started = Date.now();
    const timeout = step.timeout || 4000;
    const look = () => {
      if (cancelled) return;
      const el = document.querySelector(sel(step.target));
      if (el) {
        setTarget(el);
        // Centre a normal element; an element taller than the screen (a whole nav list on a
        // phone) is scrolled to its top instead, so the card has room below the heading
        const tall = el.getBoundingClientRect().height > window.innerHeight * 0.6;
        try { el.scrollIntoView({ block: tall ? 'start' : 'center', inline: 'nearest', behavior: 'smooth' }); } catch (e) { /* older browsers */ }
        return;
      }
      if (Date.now() - started > timeout) { setMissing(true); return; }
      setTimeout(look, POLL_MS);
    };
    look();
    return () => { cancelled = true; };
  }, [step, lookNonce]);

  // A step whose target is not on this page (role, empty list, small screen) is skipped
  useEffect(() => {
    if (missing && run) {
      const id = setTimeout(() => setRun((r) => {
        if (!r) return r;
        if (r.index + 1 >= r.steps.length) { markSeen(r.pageKey); return null; }
        return { ...r, index: r.index + 1 };
      }), 50);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [missing, run, markSeen]);

  // Follow the target as the page scrolls, resizes or re-lays out
  useEffect(() => {
    if (!target) return undefined;
    let frame;
    let last = null;
    const tick = () => {
      // The drawer closed or a tab re-rendered: drop the stale node and look for it again
      if (!document.body.contains(target)) { setTarget(null); setRect(null); setLookNonce((v) => v + 1); return; }
      const r = target.getBoundingClientRect();
      if (!sameRect(last, r)) { last = r; setRect({ top: r.top, left: r.left, width: r.width, height: r.height }); }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target]);

  // Click steps advance when the highlighted element is clicked (capture phase, so the
  // page's own handler runs too and the step moves on after the page reacts)
  useEffect(() => {
    if (!step || step.action !== 'click' || !step.target) return undefined;
    const onClick = (e) => {
      const hit = e.target && e.target.closest && e.target.closest(sel(step.target));
      if (!hit) return;
      setTimeout(() => {
        if (step.then) chainTo(step.then);
        else next();
      }, 250);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [step, next, chainTo]);

  // Keyboard: Escape closes, Enter / → moves on (not on click steps)
  useEffect(() => {
    if (!run) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); endTour('closed'); }
      else if ((e.key === 'Enter' || e.key === 'ArrowRight') && step && step.action !== 'click') { next(); }
      else if (e.key === 'ArrowLeft' && run.index > 0) { goTo(run.index - 1); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [run, step, next, goTo, endTour]);

  // Leaving the page ends its tour unless a click step is handing over to the next page.
  // Only the first path segment counts: /owner/a → /owner/b and /help/x → /help/y stay on the page.
  const page = location.pathname.split('/')[1] || '';
  const pathRef = useRef(page);
  useEffect(() => {
    if (pathRef.current === page) return;
    pathRef.current = page;
    setRun((r) => {
      if (!r) return r;
      const s = r.steps[r.index];
      if (s && s.action === 'click' && s.then) return r; // chainTo is about to clear it
      markSeen(r.pageKey);
      return null;
    });
    setTarget(null); setRect(null); setMissing(false);
  }, [page, markSeen]);

  /** Called by usePageTour on mount: start this page's tour if it is due. Returns a cleanup. */
  const maybeStart = useCallback((pageKey) => {
    currentPage.current = pageKey;
    if (!currentUser || !toursRef.current) return () => {};
    // A queued hand-over is only consumed when the tour actually starts, so a
    // re-render that cancels the timer (user data refreshing) does not lose it
    const queued = peekQueued();
    const handedOver = queued === pageKey || (queued === 'welcome' && pageKey === 'dashboard');
    const t = toursRef.current;
    let due = handedOver;
    if (!handedOver && !queued) {
      if (t.skippedAll) due = false;
      else if (t.remindAfter && Date.parse(t.remindAfter) > Date.now()) due = false;
      else due = !t.seen[pageKey];
    }
    if (!due) return () => {};
    const id = setTimeout(() => {
      if (handedOver) clearQueued();
      startTour(pageKey, { variant: queued === 'welcome' && handedOver ? 'welcome' : undefined });
    }, 700);
    return () => clearTimeout(id);
  }, [currentUser, startTour]);

  const leavePage = useCallback((pageKey) => {
    if (currentPage.current === pageKey) currentPage.current = null;
  }, []);

  const replayCurrent = useCallback(() => {
    if (!currentPage.current) return false;
    return startTour(currentPage.current);
  }, [startTour]);

  const value = useMemo(() => ({
    ready: !!currentUser && !!tours,
    tours,
    active: !!run,
    currentPageKey: () => currentPage.current,
    maybeStart, leavePage, startTour, replayCurrent, endTour, skipAll, remindLater, resetAll
  }), [currentUser, tours, run, maybeStart, leavePage, startTour, replayCurrent, endTour, skipAll, remindLater, resetAll]);

  return (
    <TourContext.Provider value={value}>
      {children}
      {run && step && (
        <TourOverlay
          run={run}
          step={step}
          rect={step.target ? rect : null}
          waiting={!!step.target && !rect}
          onNext={next}
          onBack={() => goTo(run.index - 1)}
          onSkipStep={() => (step.then ? chainTo(step.then) : next())}
          onClose={() => endTour('closed')}
          onSkipAll={() => { skipAll(); endTour('closed'); }}
          onLater={() => { remindLater(); endTour('later'); }}
        />
      )}
    </TourContext.Provider>
  );
}

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) {
    // Pages rendered outside the provider (tests) get a no-op
    return { ready: false, tours: null, active: false, currentPageKey: () => null, maybeStart: () => () => {}, leavePage: () => {}, startTour: () => false, replayCurrent: () => false, endTour: () => {}, skipAll: () => {}, remindLater: () => {}, resetAll: () => {} };
  }
  return ctx;
}

// ---- overlay + card -----------------------------------------------------------------

function TourOverlay({ run, step, rect, waiting, onNext, onBack, onSkipStep, onClose, onSkipAll, onLater }) {
  const total = run.steps.length;
  const n = run.index + 1;
  const isFirst = run.index === 0;
  const isLast = run.index === total - 1;
  const isClick = step.action === 'click';
  const [anchor, setAnchor] = useState(null);
  // The anchor box moves without a scroll event when the page re-lays out (a tab opens, the
  // smooth scroll settles), so the card is repositioned whenever the spotlight moves
  const popperRef = useRef(null);
  useEffect(() => { if (popperRef.current) popperRef.current.update(); }, [rect]);

  // A tiny fixed anchor at the spotlight's edge keeps the card attached to the
  // element even as the page scrolls (the Popper follows this box, not the DOM node)
  const hole = rect ? { top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 } : null;

  const card = (
    <Paper elevation={8} sx={{ width: 360, maxWidth: 'calc(100vw - 32px)', p: 2, borderRadius: 2 }} role="dialog" aria-label={step.title}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>{run.title} · {n} of {total}</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{step.title}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close tour"><CloseIcon fontSize="small" /></IconButton>
      </Box>
      <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-line' }}>{step.body}</Typography>
      {isClick && (
        <Typography variant="body2" color="primary" sx={{ mt: 1.5, fontWeight: 600 }}>
          {waiting ? 'Looking for that on the page…' : '👆 Click the highlighted item to continue.'}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
        {isFirst ? (
          <>
            <Link component="button" type="button" variant="caption" color="text.secondary" underline="hover" onClick={onLater}>Not now</Link>
            <Link component="button" type="button" variant="caption" color="text.secondary" underline="hover" onClick={onSkipAll}>Don't show tours</Link>
          </>
        ) : (
          <Button size="small" onClick={onBack}>Back</Button>
        )}
        <Box sx={{ flex: 1 }} />
        {isClick
          ? <Button size="small" color="inherit" onClick={onSkipStep}>Skip this step</Button>
          : <Button size="small" variant="contained" onClick={onNext}>{isLast ? 'Finish' : 'Next'}</Button>}
      </Box>
    </Paper>
  );

  // Centred card with a full dark backdrop: intro/outro steps, or while a target is awaited
  if (!hole) {
    return (
      <Box sx={{ position: 'fixed', inset: 0, zIndex: Z, bgcolor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Fade in><Box>{card}</Box></Fade>
      </Box>
    );
  }

  const blockers = [
    { top: 0, left: 0, right: 0, height: Math.max(0, hole.top) },
    { top: hole.top + hole.height, left: 0, right: 0, bottom: 0 },
    { top: hole.top, left: 0, width: Math.max(0, hole.left), height: hole.height },
    { top: hole.top, left: hole.left + hole.width, right: 0, height: hole.height }
  ];

  return (
    <>
      {/* the dark sheet with a hole: one box with a huge shadow, never catches clicks */}
      <Box sx={{ position: 'fixed', zIndex: Z, pointerEvents: 'none', borderRadius: 1.5, boxShadow: '0 0 0 9999px rgba(0,0,0,0.55), 0 0 0 3px rgba(255,255,255,0.9)', transition: 'top .2s, left .2s, width .2s, height .2s', ...hole }} />
      {/* four panels around the hole swallow clicks outside the highlighted element */}
      {blockers.map((b, i) => (
        <Box key={i} onClick={(e) => e.stopPropagation()} sx={{ position: 'fixed', zIndex: Z + 1, ...b }} />
      ))}
      {/* invisible anchor for the card */}
      <Box ref={setAnchor} sx={{ position: 'fixed', zIndex: Z, pointerEvents: 'none', ...hole }} />
      {/* The card always stays on screen: it flips to whichever side fits, and when the
          spotlight is taller or wider than the viewport (a long list on a phone) it is
          pushed inside the viewport rather than left attached to the element off-screen */}
      <Popper open={!!anchor} anchorEl={anchor} placement={step.placement || 'bottom'} sx={{ zIndex: Z + 2 }}
        popperRef={popperRef} popperOptions={{ strategy: 'fixed' }}
        modifiers={[
          { name: 'offset', options: { offset: [0, 12] } },
          { name: 'flip', options: { fallbackPlacements: ['bottom', 'top', 'right', 'left'], padding: 12 } },
          { name: 'preventOverflow', options: { padding: 12, altAxis: true, tether: false, rootBoundary: 'viewport' } }
        ]}>
        <Fade in><Box sx={{ p: 0.5 }}>{card}</Box></Fade>
      </Popper>
    </>
  );
}

export default TourProvider;
